import {
    WorkbenchPlugin,
    WorkbenchPluginId, WorkbenchViewConfig, WorkbenchViewKind
} from '../../shared/types';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {faDesktop, faMobile} from '@fortawesome/pro-light-svg-icons';
import {DeployedDevicesViewComponent} from './views/deployed-devices-view.component';
import {constructRequirementsForDeployable, Deployable} from '@webmate-web-core/shared/types/devices/device-template';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {BrowserSessionsViewComponent} from './views/browsersessions-view.component';
import {WorkbenchCommandRegistry} from '../../shared/services/workbench-command-registry.service';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';

@Injectable()
export class DevicePlugin extends WorkbenchPlugin {
    id: WorkbenchPluginId = 'device';
    projectId: ProjectId;
    views: WorkbenchViewKind[];

    static readonly DeployedDeviceViewId = 'device.view.deployedDevice';
    static readonly DeployedDesktopDeviceViewId = 'device.view.deployedDesktopDevice';
    static readonly BrowserSessionsViewId = 'device.view.browserSessions';

    private mobileViewContainerId = 'device.mobile-viewcontainer';
    private desktopViewContainerId = 'device.desktop-viewcontainer';

    constructor(private readonly commandRegistry: WorkbenchCommandRegistry,
                private readonly deviceService: DeviceService,
                readonly layoutManager: WorkbenchLayoutManager,
                private readonly workbenchContextService: WorkbenchContextService) {
        super(layoutManager);

        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => this.projectId = context.project);
        this.views = [
            {
                id: DevicePlugin.DeployedDeviceViewId,
                component: DeployedDevicesViewComponent,
                name: 'Deployed Devices',
                expandedByDefault: true,
                defaultViewContainer: this.mobileViewContainerId,
                payload: {
                    data: {
                        platformFamily: 'mobile'
                    }
                }
            },
            {
                id: DevicePlugin.DeployedDesktopDeviceViewId,
                component: DeployedDevicesViewComponent,
                name: 'Deployed Devices',
                expandedByDefault: true,
                defaultViewContainer: this.desktopViewContainerId,
                payload: {
                    data: {
                        platformFamily: 'desktop'
                    }
                }
            },
            {
                id: DevicePlugin.BrowserSessionsViewId,
                component: BrowserSessionsViewComponent,
                name: 'Browser Sessions',
                expandedByDefault: true,
                defaultViewContainer: this.desktopViewContainerId,
            },
        ];
    }

    onWorkbenchDestroy(): void {
    }

    onWorkbenchInit(): Observable<void> {
        this.layoutManager.registerViewContainer({
            type: 'NormalViewContainer',
            id: this.mobileViewContainerId,
            name: 'Mobile',
            icon: {
                type: 'faIcon',
                instance: faMobile,
            },
            allowedPanels: ['left-top', 'left-bottom'],
            defaultPanel: 'left-top',
            displayMode: 'stacked',
            toolbarCommandId: 'device:toggleMobileViewContainer'
        });

        this.layoutManager.registerViewContainer({
            type: 'NormalViewContainer',
            id: this.desktopViewContainerId,
            name: 'Desktop',
            icon: {
                type: 'faIcon',
                instance: faDesktop,
            },
            allowedPanels: ['left-top', 'left-bottom'],
            defaultPanel: 'left-top',
            displayMode: 'stacked',
            toolbarCommandId: 'device:toggleDesktopViewContainer'
        });

        this.commandRegistry.registerCommand(
            'device:deploy',
            {
                internal: true,
            },
            (params: any) => {
                const deployable = params.deployable as Deployable;
                const requirements = constructRequirementsForDeployable(deployable, false);
                return this.deviceService.requestDeployment$(this.projectId, requirements, deployable.modelName);
            }
        );

        this.commandRegistry.registerCommand(
            'device:toggleMobileViewContainer',
            {
                name: 'Mobile Devices',
                icon: {
                    type: 'faIcon',
                    instance: faMobile,
                },
                description: 'Toggle Device View',
                statusBindings: {
                    'active': {
                        'type': 'store',
                        'storeKey': `viewcontainers.${this.mobileViewContainerId}.status.visible`,
                    }
                }
            },
            () => {
                this.layoutManager.toggleViewContainer(this.mobileViewContainerId, true);
            }
        );

        this.commandRegistry.registerCommand(
            'device:toggleDesktopViewContainer',
            {
                name: 'Desktop Devices',
                icon: {
                    type: 'faIcon',
                    instance: faDesktop,
                },
                description: 'Toggle Desktop device view',
                statusBindings: {
                    'active': {
                        'type': 'store',
                        'storeKey': `viewcontainers.${this.desktopViewContainerId}.status.visible`,
                    }
                }
            },
            () => {
                this.layoutManager.toggleViewContainer(this.desktopViewContainerId, true);
            }
        );

        return of(undefined);
    }

}
